// A semicircle donut with two segments and a headline number in the arch, as plain SVG rather than
// a chart library for two arcs.

import { Tooltip } from "../ui/primitives.js";

export type DonutColor = "win" | "loss" | "good" | "evil";

type Segment = { label: string; value: number; color: DonutColor };

// Spelled out in full so Tailwind sees each class in the source and generates it.
const STROKE_CLASS: Record<DonutColor, string> = {
  win: "stroke-win",
  loss: "stroke-evil",
  good: "stroke-good",
  evil: "stroke-evil",
};
const SWATCH_CLASS: Record<DonutColor, string> = {
  win: "bg-win",
  loss: "bg-evil",
  good: "bg-good",
  evil: "bg-evil",
};

type Props = {
  /** The headline in the arch — usually the total, but the caller decides. */
  headline: number | string;
  caption: string;
  /** Drawn left to right; the first segment starts at the left end of the arch. */
  segments: [Segment, Segment];
};

const CX = 100;
const CY = 100;
const R = 76;
const STROKE = 26;
/** A surface-coloured gap between the two segments, ~2px at this radius. */
const GAP_DEG = 1.6;

function point(deg: number): string {
  const rad = (deg * Math.PI) / 180;
  return `${(CX + R * Math.cos(rad)).toFixed(2)} ${(CY - R * Math.sin(rad)).toFixed(2)}`;
}

/** Arc from `from` to `to` degrees (180 is the left end, 0 the right), sweeping over the top. */
function arc(from: number, to: number): string {
  const large = from - to > 180 ? 1 : 0;
  return `M ${point(from)} A ${R} ${R} 0 ${large} 1 ${point(to)}`;
}

export function HalfDonut({ headline, caption, segments }: Props) {
  const [a, b] = segments;
  const total = a.value + b.value;
  const both = a.value > 0 && b.value > 0;
  const split = total === 0 ? 180 : 180 - (a.value / total) * 180;
  const gap = both ? GAP_DEG / 2 : 0;

  return (
    <figure className="m-0 flex flex-col items-center">
      <svg viewBox="0 0 200 104" className="w-full max-w-56" role="img" aria-label={`${headline} ${caption}`}>
        {/* Track: what an empty record looks like, and the gap colour showing through. */}
        <path d={arc(180, 0)} fill="none" strokeWidth={STROKE} className="stroke-ink-line/50" strokeLinecap="butt" />
        {a.value > 0 && (
          <Tooltip content={`${a.value} ${a.label}`}>
            <path d={arc(180, split + gap)} fill="none" strokeWidth={STROKE} className={STROKE_CLASS[a.color]} />
          </Tooltip>
        )}
        {b.value > 0 && (
          <Tooltip content={`${b.value} ${b.label}`}>
            <path d={arc(split - gap, 0)} fill="none" strokeWidth={STROKE} className={STROKE_CLASS[b.color]} />
          </Tooltip>
        )}
        <text
          x={CX}
          y={CY - 2}
          textAnchor="middle"
          className="fill-parchment font-semibold"
          style={{ fontSize: 34 }}
        >
          {headline}
        </text>
      </svg>
      <figcaption className="-mt-1 text-center">
        <div className="text-xs uppercase tracking-wide text-parchment-dim">{caption}</div>
        <dl className="mt-1.5 flex justify-center gap-4 text-xs text-parchment">
          {segments.map((s) => (
            <div key={s.label} className="flex items-center gap-1.5">
              <span aria-hidden className={`inline-block size-2 rounded-full ${SWATCH_CLASS[s.color]}`} />
              <dd className="m-0 tabular-nums">{s.value}</dd>
              <dt className="text-parchment-dim">{s.label}</dt>
            </div>
          ))}
        </dl>
      </figcaption>
    </figure>
  );
}
